import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { AlertCircle, Settings } from "lucide-react";
import RecordRTC from "recordrtc";
import {
  getHashToLocalStorage,
  getTokenToLocalStorage,
} from "../../Utils/local-storage";
import { Alert, Button, Col, ProgressBar, Row } from "react-bootstrap";
import { Layout } from "../../Components/layout";

import "./styles.css";

export function BiometriaCaptura() {
  const [message, setMessage] = useState("Posicione seu rosto");
  const [isRecording, setIsRecording] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isFaceReady, setIsFaceReady] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [now, setNow] = useState(0);

  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const landmarkerRef = useRef(null);
  const requestRef = useRef(null);
  const socketRef = useRef(null);

  const isInternalRecording = useRef(false);
  const isRestarting = useRef(false);
  const faceStabilityCount = useRef(0);

  const livenessRef = useRef({ sorriu: 0, piscou: 0 });
  const bestFrameRef = useRef(null);
  const lastVideoBlob = useRef(null);

  const navigate = useNavigate();

  const hash = getHashToLocalStorage();
  const CHUNK_SIZE = 1024 * 100; // 100KB

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const sliceAndSendVideo = async (blob, socket, onProgress) => {
    let offset = 0;
    const totalSize = blob.size;

    while (offset < blob.size) {
      const isLast = offset + CHUNK_SIZE >= blob.size;

      if (isLast) break;

      const chunk = blob.slice(offset, offset + CHUNK_SIZE);

      if (socket.readyState === WebSocket.OPEN) {
        const base64Data = await blobToBase64(chunk);
        socket.send(
          JSON.stringify({
            action: "VIDEO_CHUNK",
            payload: { video: base64Data },
          }),
        );

        const percentComplete = Math.round((offset / totalSize) * 90);
        onProgress(percentComplete);
      }

      offset += CHUNK_SIZE;
      await new Promise((r) => setTimeout(r, 10));
    }

    return blob.slice(offset);
  };

  const sendDataViaSocket = async () => {
    if (!capturedImage || !lastVideoBlob.current) return;
    setIsSending(true);
    setMessage("Conectando ao servidor...");

    const socket = new WebSocket(
      `${process.env.REACT_APP_WS_VALIDATION}?token=${getTokenToLocalStorage()}`,
    );
    socketRef.current = socket;

    const wsTimeout = setTimeout(() => {
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
        setValidationError(
          "Tempo esgotado ao conectar ao servidor. Tente novamente.",
        );
        setIsSending(false);
      }
    }, 30000);

    socket.onopen = () => {
      clearTimeout(wsTimeout);
      socket.send(
        JSON.stringify({ action: "GET_BIOMETRIA", payload: { hash } }),
      );
    };

    socket.onmessage = async (event) => {
      const response = JSON.parse(event.data);

      if (response.data)
        if (response.message === "Conexão estabelecida com sucesso!") return;

      if (
        response.code === 200 &&
        response.success === true &&
        response.msg === "Upload e processamento finalizados"
      ) {
        clearTimeout(wsTimeout);
        setUploadProgress(100);
        setMessage("Sucesso!");
        setIsFinished(true);
        socket.close();
        stopCamera();
        localStorage.setItem("@flexgr-validacao-facial:sucesso", "true");

        return;
      }

      if (response.code !== 200 || response.success === false) {
        setValidationError(
          response.msg || "Não foi possível validar sua biometria.",
        );
        setIsSending(false);
        socket.close();
        return;
      }

      if (
        response.code === 200 &&
        response.idBiometria &&
        response.msg !== "Upload e processamento finalizados"
      ) {
        if (message === "Transmitindo vídeo...") return;

        setMessage("Transmitindo vídeo...");

        try {
          const finalChunkBlob = await sliceAndSendVideo(
            lastVideoBlob.current,
            socket,
            (p) => setUploadProgress(p),
          );

          if (socket.readyState === WebSocket.OPEN) {
            const finalBase64 = await blobToBase64(finalChunkBlob);
            const photoBase64 = capturedImage.includes(",")
              ? capturedImage.split(",")[1]
              : capturedImage;

            const body = {
              action: "UPLOAD_COMPLETE",
              payload: {
                video: finalBase64,
                videoType: lastVideoBlob.current?.type,
                photo: photoBase64,
                imageType: bestFrameRef.current?.type,
                hash: getHashToLocalStorage(),
                // idBiometria: 8176,
              },
            };
            socket.send(JSON.stringify(body));
            setUploadProgress(95);
            setMessage("Validando biometria...");
          }
        } catch (err) {
          console.error("Erro no envio:", err);
          setValidationError("Erro ao processar os arquivos da biometria.");
        }
      }
    };

    socket.onerror = () => {
      setMessage("Erro de conexão.");
      setIsSending(false);
    };

    socket.onclose = () => {
      setIsSending(false);
    };
  };

  const setupDetector = async () => {
    try {
      setCameraError(null);
      if (!landmarkerRef.current) {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
        );
        landmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => videoRef.current?.play();
      }
      predict();
    } catch (error) {
      console.error("Erro Câmera:", error);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setCameraError(
          "O acesso à câmera foi negado. Precisamos dela para realizar sua biometria facial.",
        );
      } else {
        setCameraError(
          "Não conseguimos acessar sua câmera. Verifique se ela está sendo usada por outro app.",
        );
      }
    }
  };

  const predict = async () => {
    if (
      videoRef.current &&
      videoRef.current?.readyState >= 2 &&
      landmarkerRef.current
    ) {
      const results = landmarkerRef.current.detectForVideo(
        videoRef.current,
        Date.now(),
      );
      const aligned = checkFaceAlignment(results);
      setIsFaceReady(aligned);

      if (isInternalRecording.current && !aligned && !isRestarting.current) {
        handleForceRestart();
      }

      if (
        isInternalRecording.current &&
        aligned &&
        results.faceBlendshapes?.length > 0
      ) {
        const shapes = results.faceBlendshapes[0].categories;
        const smile =
          (shapes.find((s) => s.categoryName === "mouthSmileLeft")?.score +
            shapes.find((s) => s.categoryName === "mouthSmileRight")?.score) /
          2;
        if (smile > livenessRef.current.sorriu && smile > 0.2) {
          takeSnapshot().then((blob) => {
            if (blob) bestFrameRef.current = blob;
          });
        }
        livenessRef.current.sorriu = Math.max(
          livenessRef.current.sorriu,
          smile,
        );
      }
    }
    requestRef.current = requestAnimationFrame(predict);
  };

  const checkFaceAlignment = (results) => {
    if (!results.faceLandmarks || results.faceLandmarks?.length === 0)
      return false;
    const landmarks = results.faceLandmarks[0];
    const nose = landmarks[4],
      leftEye = landmarks[33],
      rightEye = landmarks[263],
      chin = landmarks[152],
      forehead = landmarks[10];
    const yaw = Math.abs(nose.x - leftEye.x) / Math.abs(nose.x - rightEye.x);
    const pitch = Math.abs(nose.y - forehead.y) / Math.abs(nose.y - chin.y);
    const isAligned = yaw > 0.7 && yaw < 1.4 && pitch > 0.6 && pitch < 1.6;

    if (isAligned) faceStabilityCount.current += 1;
    else faceStabilityCount.current = 0;

    return faceStabilityCount.current > 10;
  };

  const handleReset = () => {
    setValidationError(null);
    setIsFinished(false);
    setIsSending(false);
    setCapturedImage(null);
    setShowPreview(false);
    setUploadProgress(0);
    setMessage("Posicione seu rosto");

    bestFrameRef.current = null;
    lastVideoBlob.current = null;
    livenessRef.current = { sorriu: 0, piscou: 0 };
    faceStabilityCount.current = 0;

    setIsRecording(false);
    setIsZoomed(false);
    isInternalRecording.current = false;
    isRestarting.current = false;

    setupDetector();
  };

  const handleStartProcess = () => {
    setIsRecording(true);
    isRestarting.current = false;
    setMessage("Aguardando alinhamento...");
  };

  const getSupportedMimeType = () => {
    const types = [
      "video/mp4;codecs=avc1",
      "video/mp4",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];

    if (typeof MediaRecorder === "undefined") return undefined;

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        if (type.includes("mp4")) return "video/mp4";
        return type;
      }
    }

    return undefined;
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
        console.log("Track da câmera interrompida");
      });
      videoRef.current.srcObject = null;
    }
  };

  const stopRecording = () => {
    isInternalRecording.current = false;
    if (recorderRef.current && recorderRef.current.getState() !== "inactive") {
      recorderRef.current.stopRecording(async () => {
        lastVideoBlob.current = recorderRef.current.getBlob();
        if (!bestFrameRef.current) bestFrameRef.current = await takeSnapshot();
        const reader = new FileReader();
        reader.readAsDataURL(bestFrameRef.current);
        reader.onloadend = () => {
          setCapturedImage(reader.result);
          setShowPreview(true);
          setIsRecording(false);
          setIsZoomed(false);
        };
      });
    }
  };

  const startRecording = () => {
    if (!isFaceReady || isRestarting.current) return;
    const stream = videoRef.current?.srcObject;
    if (!stream) return;

    const mimeType = getSupportedMimeType();

    isInternalRecording.current = true;
    recorderRef.current = new RecordRTC(stream, {
      type: "video",
      mimeType: mimeType,
      recorderType: mimeType?.includes("mp4")
        ? RecordRTC.MediaStreamRecorder
        : undefined,
    });
    recorderRef.current.startRecording();
    setMessage("Mantenha o rosto parado");

    setTimeout(() => {
      if (!isInternalRecording.current) return;
      setIsZoomed(true);
      setMessage("Aproxime e sorria!");
      setTimeout(() => isInternalRecording.current && stopRecording(), 3000);
    }, 2000);
  };

  const handleForceRestart = () => {
    if (isRestarting.current) return;
    isRestarting.current = true;
    isInternalRecording.current = false;
    if (recorderRef.current && recorderRef.current.getState() !== "inactive") {
      try {
        recorderRef.current.stopRecording();
      } catch (e) {
        console.log(e);
      }
    }
    setCountdown(null);
    setIsZoomed(false);
    setMessage("Rosto desalinhado! Reiniciando...");
    setTimeout(() => {
      isRestarting.current = false;
      if (!showPreview) setMessage("Posicione seu rosto");
    }, 2000);
  };

  const takeSnapshot = () => {
    return new Promise((res) => {
      if (!videoRef.current) return res(null);
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
      canvas.toBlob(res, "image/jpeg", 0.9);
    });
  };

  useEffect(() => {
    setNow(0);
    const interval = setInterval(() => {
      setNow((oldValue) => {
        if (oldValue >= 100) {
          clearInterval(interval);
          return 100;
        }
        return oldValue + 100;
      });
    }, 500);
  }, []);

  useEffect(() => {
    let timer;
    if (
      isRecording &&
      isFaceReady &&
      countdown === null &&
      !isInternalRecording.current &&
      !isRestarting.current
    ) {
      setCountdown(3);
    }
    if (isRecording && countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    } else if (isRecording && countdown === 0) {
      setCountdown(null);
      startRecording();
    }
    if (
      isRecording &&
      !isFaceReady &&
      countdown !== null &&
      !isInternalRecording.current
    ) {
      setCountdown(null);
      setMessage("Aguardando alinhamento...");
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, isFaceReady, countdown]);

  useEffect(() => {
    if (!showPreview) {
      setupDetector();
    } else {
      const stream = videoRef.current?.srcObject;
      stream?.getTracks().forEach((t) => t.stop());
    }
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview]);

  useEffect(() => {
    const isBiometricFinished = localStorage.getItem(
      "@flexgr-validacao-facial:sucesso",
    );

    if (isBiometricFinished === "true") {
      navigate("/biometria/instrucoes", { replace: true });
    }
  }, [navigate]);

  if (cameraError) {
    return (
      <Layout>
        <Row className="d-flex flex-column gap-3">
          <Col className="icon_container border card_info p-3">
            <AlertCircle className="icon_style_lg text-danger" />
          </Col>

          <Col className="text-center">
            <h1 className="title">Permissão Negada</h1>

            <p className="subtitle">{cameraError}</p>
          </Col>

          <Alert
            variant="warning"
            className="my-2 p-4 card_info border-warning small text-left"
          >
            <p className="mb-1 d-flex align-items-center gap-2 font-bold">
              <Settings className="icon_style_sm" /> Como habilitar:
            </p>
            <ol className="ms-2">
              <li>
                Toque no ícone de <strong>ajustes/cadeado</strong> ao lado da
                URL (topo da tela).
              </li>
              <li>
                Encontre a opção <strong>Câmera</strong> ou{" "}
                <strong>Permissões</strong>.
              </li>
              <li>
                Altere para <strong>Permitir</strong> e atualize a página.
              </li>
            </ol>
          </Alert>

          <Button
            onClick={() => window.location.reload()}
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
          >
            Tentar Novamente
          </Button>

          <Button
            onClick={() => navigate("/biometria/cancelar")}
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
            variant="danger"
          >
            Cancelar
          </Button>
        </Row>
      </Layout>
    );
  }

  if (validationError) {
    return (
      <Layout>
        <Row className="d-flex flex-column gap-3">
          <Alert
            variant="danger"
            className="icon_container rounded-circle border-danger p-3"
          >
            <AlertCircle className="icon_style_lg text-danger" />
          </Alert>

          <Col className="text-center">
            <h2 className="title">Falha na Validação</h2>

            <p className="subtitle">{validationError}</p>
          </Col>
          <Alert
            variant="warning"
            className="my-2 p-4 card_info border-warning small text-left"
          >
            <p className="mb-1 d-flex align-items-center gap-2 font-bold">
              Dicas para sucesso:
            </p>
            <ul className="ms-2">
              <li>Procure um ambiente bem iluminado.</li>
              <li>Remova óculos de sol ou boné.</li>
              <li>Mantenha o celular na altura dos olhos.</li>
            </ul>
          </Alert>

          <Button
            onClick={handleReset}
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
          >
            Tentar Novamente
          </Button>

          <Button
            onClick={() => navigate("/biometria/cancelar")}
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
            variant="danger"
          >
            Cancelar
          </Button>
        </Row>
      </Layout>
    );
  }

  if (isFinished) {
    return (
      <Layout
        cardStyle={{ minHeight: "50vh", margin: "auto" }}
        marginTop="120px"
      >
        <Row className="d-flex flex-column align-items-center justify-content-center gap-3">
          <Alert
            variant="success"
            className="rounded-circle p-4 border-success p-4 "
            style={{ width: "fit-content" }}
          >
            <svg
              className="success_icon text-success"
              viewBox="0 0 24 24"
              strokeWidth="3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </Alert>

          <Col className="text-center">
            <h2 className="title">Tudo certo!</h2>
            <p className="subtitle">Sua biometria foi validada com sucesso.</p>
          </Col>

          <ProgressBar
            variant="success"
            now={now}
            className="p-0 rounded-pill"
            style={{ height: "8px" }}
          />
        </Row>
      </Layout>
    );
  }

  if (showPreview && capturedImage) {
    return (
      <Layout hasCamera={true}>
        <Row className="d-flex flex-column align-items-center justify-content-center">
          <h3 className="title text-center">
            {isSending ? "Validando..." : "Confira sua foto"}
          </h3>

          <Col className="image_container border">
            <img
              src={capturedImage}
              alt="Preview"
              style={isSending ? { opacity: "0.5" } : {}}
            />
            {isSending && <div className="imagem_msg">{message}</div>}
          </Col>

          <Col className={`mt-5 ${isSending ? "d-block" : "d-none"}`}>
            <div className="d-flex justify-content-between mb-2">
              <span className="small font-bold text-muted text-uppercase">
                Progresso
              </span>
              <span className="small font-bold">{uploadProgress}%</span>
            </div>
            <ProgressBar
              variant="success"
              now={uploadProgress}
              className="p-0 rounded-pill"
              style={{ height: "8px" }}
            />
          </Col>

          <Col className="d-flex flex-column gap-3 mt-3">
            <Button
              onClick={sendDataViaSocket}
              disabled={isSending}
              className="py-2 d-flex align-items-center justify-content-center gap-2"
              size="lg"
            >
              {isSending ? "Enviando..." : "Confirmar e Enviar"}
            </Button>

            {!isSending && (
              <Button
                onClick={handleReset}
                className="py-2 d-flex align-items-center justify-content-center gap-2"
                size="lg"
                variant="outline-primary"
              >
                Tentar Novamente
              </Button>
            )}
          </Col>
        </Row>
      </Layout>
    );
  }

  return (
    <Layout hasCamera={true}>
      <Row className="d-flex flex-column align-items-center p-3 gap-4">
        <Col className="mb-4" style={{ height: "50px" }}>
          <h3 className="text-uppercase font-bold m-0 text-center">
            {countdown !== null && countdown > 0
              ? `Iniciando em ${countdown}s`
              : message}
          </h3>
        </Col>

        <Col className="d-flex justify-content-center align-items-center position-relative camera_col">
          <div
            className={`position-relative rounded-pill overflow-hidden bg-dark ${isFaceReady ? "" : "border-danger"} `}
            style={{
              border: "6px solid #dee2e6",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scaleX(-1)",
              }}
            />
            <div
              className="position-absolute d-flex justify-content-center align-items-center"
              style={{ inset: "0" }}
            >
              <div
                className={isZoomed ? "border_isZoomed" : "border_notZoomed"}
              />
            </div>
          </div>
        </Col>

        {!isRecording && (
          <Button
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
            onClick={() => {
              window.location.hash = "captura";
              handleStartProcess();
            }}
          >
            Iniciar validação
          </Button>
        )}
      </Row>
    </Layout>
  );
}
